import { getCostForQuantityGeneric, getQuantityUpToCostGeneric } from "@/hooks/reservoir/order-book-utilities";
import { usdcContractAddress } from "@talentir/shared/abis/erc-20";
import { tokenContractAddress } from "@talentir/shared/abis/erc-1155";
import { SHARE_TO_PERCENT_CONVERSION } from "@talentir/shared/config";
import { useQuery } from "@tanstack/react-query";
import Big from "big.js";
import { useCallback, useMemo } from "react";
import { getAddress } from "viem";
import { useReservoirkitClient } from "../use-reservoirkit-client";
import { useGetReservoirWallet } from "./use-get-reservoir-wallet";

export function useReservoirBuyPrecheck(tokenId?: string) {
    const reservoirClient = useReservoirkitClient();
    const reservoir = useGetReservoirWallet();

    const { data: pathRaw } = useQuery({
        queryKey: [tokenId, reservoir?.address, reservoir, "reservoir.buy.precheck"],
        enabled: tokenId != null,
        queryFn: async () => {
            if (reservoir == null) {
                return reservoir;
            }

            try {
                const result = await reservoirClient?.actions.buyToken({
                    items: [
                        {
                            token: `${tokenContractAddress}:${tokenId}`,
                            // check for full quantity, so we get all orders
                            quantity: 1_000_000,
                        },
                    ],
                    wallet: reservoir.wallet,
                    precheck: true,
                    options: {
                        usePermit: true,
                        currency: usdcContractAddress,
                        skipBalanceCheck: true,
                        onlyPath: true,
                        partial: true,
                    },
                    onProgress: () => {},
                });

                if (result !== true) {
                    return result?.path;
                }
            } catch {
                return null;
            }
        },
    });

    const path = useMemo(() => {
        if (pathRaw == null) return pathRaw;

        return pathRaw?.filter((order) => {
            if (order.currency == null) {
                return false;
            }

            const isUsdc = getAddress(order.currency) === getAddress(usdcContractAddress);

            const orderDecimals = order.currencyDecimals;

            const costOneToken = order.totalRawPrice ?? "0";
            const costOnePercent = Big(costOneToken)
                .mul(Big(SHARE_TO_PERCENT_CONVERSION))
                .div(Big(10).pow(orderDecimals ?? 0));

            const isRidiculouslyExpensive = costOnePercent.gt(1_000);
            return isUsdc && !isRidiculouslyExpensive;
        });
    }, [pathRaw]);

    const getCostForQuantity = useCallback(
        (quantity: bigint) => {
            if (path == null) {
                return;
            }

            return getCostForQuantityGeneric(path, quantity);
        },
        [path],
    );

    const getQuantityUpToCost = useCallback(
        (cost: bigint) => {
            if (path == null) {
                return;
            }

            return getQuantityUpToCostGeneric(path, cost);
        },
        [path],
    );

    const cheapestPriceOnePercet = useMemo(() => {
        if (path === undefined) {
            return;
        }

        const order = path?.[0];
        if (
            path === null ||
            order?.currency == null ||
            path?.length === 0 ||
            getAddress(order.currency) !== usdcContractAddress
        ) {
            return null;
        }

        const orderDecimals = order.currencyDecimals;

        const costOneToken = order.totalRawPrice ?? "0";
        const costOnePercent = Big(costOneToken)
            .mul(Big(SHARE_TO_PERCENT_CONVERSION))
            .div(Big(10).pow(orderDecimals ?? 0));
        return costOnePercent.toNumber().toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
        });
    }, [path]);

    const availableQuantity = useMemo(() => {
        const quantity = path?.reduce((acc, order) => {
            if (order.currency == null || getAddress(order.currency) !== usdcContractAddress) {
                return acc;
            }

            return acc + BigInt(order.quantity ?? "0");
        }, 0n);

        const percent = Big(quantity?.toString() ?? "0")
            .div(Big(SHARE_TO_PERCENT_CONVERSION))
            .toString();

        return {
            raw: quantity,
            percent,
            percentString: `${percent.toString()} %`,
        };
    }, [path]);

    return { getCostForQuantity, getQuantityUpToCost, cheapestPriceOnePercet, availableQuantity };
}
